import { Suspense, useEffect, useState } from 'react';
import { lazyScreen } from '../../app/lazyScreen';
import { ArrowLeftRight, CalendarClock, CloudCheck, CloudOff, Download, FileUp, Gem, Megaphone, Palette, ShieldCheck, Tags } from 'lucide-react';
import { usePremium } from '../../premium/PremiumProvider';
import { PremiumTag } from '../../premium/PremiumScreen';
import { useTranslation } from 'react-i18next';
import { useServices, type ThemePreference } from '../../app/services';
import { LANGUAGES, isLanguage } from '../../i18n/i18n';
import { PRIVACY_URL, openExternal } from '../../app/links';
import { Card, List, ListRow, ScreenTitle, SegmentedControl, SelectField, Section, Switch } from '../../ui';
import { BudgetSwitcher } from '../spaces/BudgetSwitcher';
import { useSync } from '../../sync/SyncProvider';
import { useStatusText } from '../account/AccountScreen';
const ExportSheet = lazyScreen(
  () => import('./ExportSheet'),
  (m) => m.ExportSheet,
);

export function MoreScreen() {
  const { t } = useTranslation();
  const { language, setLanguage, theme, setTheme, platform, db, schemaVersion, sampleData, setSampleData, activeSpace } = useServices();
  const [switching, setSwitching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const sync = useSync();
  const syncText = useStatusText(sync.status, sync.lastSyncedAt, sync.pending - sync.held);
  const premium = usePremium();
  const adPrivacy = useAdPrivacyChoices();

  return (
    <>
      <ScreenTitle>{t('nav.more')}</ScreenTitle>

      <Section divided={false}>
        <Card flush>
          <List inset>
            <ListRow
              leading={<Gem size={18} aria-hidden />}
              title={t('premium.title')}
              subtitle={premium.active ? t('premium.active') : t('premium.moreBody')}
              to="/premium"
            />
          </List>
        </Card>
      </Section>

      {sync.available && (
        <Section title={t('account.section')} divided={false}>
          <Card flush>
            <List inset>
              <ListRow
                leading={sync.account ? <CloudCheck size={18} aria-hidden /> : <CloudOff size={18} aria-hidden />}
                title={t('account.title')}
                subtitle={sync.account ? `${sync.account.email} · ${syncText}` : t('account.moreBody')}
                to="/more/account"
              />
            </List>
          </Card>
        </Section>
      )}

      <Section title={t('more.appearance')} divided={!sync.available ? false : true}>
        <div className="stack">
          <SelectField
            label={t('more.language')}
            value={language}
            onChange={(e) => {
              if (isLanguage(e.target.value)) void setLanguage(e.target.value);
            }}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.nativeName}
              </option>
            ))}
          </SelectField>
          <SegmentedControl<ThemePreference>
            label={t('more.appearance')}
            value={theme}
            onChange={(v) => void setTheme(v)}
            options={[
              { value: 'system', label: t('more.theme.system') },
              { value: 'light', label: t('more.theme.light') },
              { value: 'dark', label: t('more.theme.dark') },
            ]}
          />
        </div>
      </Section>

      <Section title={activeSpace?.name ?? t('nav.budgets')}>
        <Card flush>
          <List inset>
            <ListRow
              leading={<ArrowLeftRight size={18} aria-hidden />}
              title={t('spaces.moreTitle')}
              subtitle={activeSpace ? t('spaces.moreBody', { name: activeSpace.name }) : undefined}
              onClick={() => setSwitching(true)}
            />
            <ListRow leading={<CalendarClock size={18} aria-hidden />} title={t('recurring.title')} subtitle={t('recurring.moreBody')} to="/more/recurring" />
            <ListRow
              leading={<FileUp size={18} aria-hidden />}
              title={t('import.title')}
              titleAddon={<PremiumTag />}
              subtitle={t('import.moreBody')}
              to="/import"
            />
            <ListRow leading={<Tags size={18} aria-hidden />} title={t('catalog.title')} subtitle={t('catalog.manageBody')} to="/more/categories" />
          </List>
        </Card>
      </Section>

      <BudgetSwitcher open={switching} onClose={() => setSwitching(false)} />

      <Section title={t('export.section')}>
        <Card flush>
          <List inset>
            <ListRow leading={<Download size={18} aria-hidden />} title={t('export.row')} subtitle={t('export.rowBody')} onClick={() => setExporting(true)} />
          </List>
        </Card>
      </Section>
      {exporting && (
        <Suspense fallback={null}>
          <ExportSheet open onClose={() => setExporting(false)} />
        </Suspense>
      )}

      <Section title={t('home.sample')}>
        <Card flush>
          <Switch label={t('home.showSample')} description={t('home.showSampleBody')} checked={sampleData} onChange={(on) => void setSampleData(on)} />
        </Card>
      </Section>

      <Section title={t('more.about')}>
        <Card flush>
          <List inset>
            <ListRow title={t('more.platform')} trailing={<span data-testid="platform">{platform}</span>} />
            <ListRow
              title={t('more.storage')}
              subtitle={<span className={db.persistent ? undefined : 'tone-up'}>{db.persistent ? t('more.storagePersistent') : t('more.storageMemory')}</span>}
              trailing={<span data-testid="storage-kind">{db.kind}</span>}
            />
            <ListRow title={t('more.schema')} trailing={<span data-testid="schema-version">{schemaVersion}</span>} />
            <ListRow title={t('more.version')} trailing={__APP_VERSION__} />
            <ListRow
              leading={<ShieldCheck size={18} aria-hidden />}
              title={t('more.privacy')}
              subtitle={t('more.privacyBody')}
              onClick={() => openExternal(PRIVACY_URL)}
            />
            {adPrivacy && (
              <ListRow leading={<Megaphone size={18} aria-hidden />} title={t('more.adPrivacy')} subtitle={t('more.adPrivacyBody')} onClick={adPrivacy} />
            )}
            {import.meta.env.DEV && <ListRow leading={<Palette size={18} aria-hidden />} title={t('more.designSystem')} to="/design" />}
          </List>
        </Card>
      </Section>
    </>
  );
}

/**
 * Where the law asks for consent to ads (the EEA, the UK, Switzerland),
 * Google requires a way to change it at any time: this returns the action
 * that opens its privacy choices, or null (Premium, the web, elsewhere).
 */
function useAdPrivacyChoices(): (() => void) | null {
  const { ads } = useServices();
  const premium = usePremium();
  const [required, setRequired] = useState(false);
  const eligible = Boolean(ads) && premium.ready && !premium.has('noAds');
  useEffect(() => {
    if (!ads || !eligible) return setRequired(false);
    let live = true;
    void ads.consent().then((c) => live && setRequired(c.privacyChoices));
    return () => {
      live = false;
    };
  }, [ads, eligible]);
  return ads && eligible && required ? () => void ads.openPrivacyChoices() : null;
}
